import React, { ReactNode } from "react";
import { connect } from "react-redux";

import Input from "antd/es/input";
import cn from "classnames";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { authCommonStyles as commonStyles } from "@mapmycustomers/auth";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";
import { Button, FormItem } from "@mapmycustomers/ui";

import Path from "@app/enum/Path";
import SignupField from "@app/scene/auth/component/Register/enum/SignupField";
import BaseStepProps from "@app/scene/auth/component/Register/types/BaseStepProps";
import {
  usePhoneValidationRules,
  useRequiredValidation,
  useUsernameValidationRules,
} from "@app/scene/auth/component/Register/utils/validation-hooks";
import { doesUserExist, isUserExistsLoading } from "@app/store/auth";
import { userExists } from "@app/store/auth/actions";
import { RootState } from "@app/store/rootReducer";

import styles from "../../Steps.module.scss";

import useEmailValidationStatus from "./useEmailValidationStatus";

const emailRegex = /\S+@\S+\.\S+/;

interface Props extends BaseStepProps {
  checkingIfUserExists: boolean;
  doesUserExist: boolean;
  email?: string;
  title: ReactNode;
  userExists: typeof userExists.request;
}

const ContactInfoStep = ({
  checkingIfUserExists,
  className,
  doesUserExist,
  email,
  form,
  isValid,
  onNextClick,
  title,
  userExists,
}: Props) => {
  const intl = useIntl();

  const checkExistingUser = useDebouncedCallback(
    [
      (e) => {
        const value = e.target.value;
        if (emailRegex.test(value)) {
          userExists({ username: value });
        }
      },
      500,
    ],
    [userExists]
  );

  const usernameValidationRules = useUsernameValidationRules(
    intl,
    doesUserExist,
    checkingIfUserExists
  );
  const requiredValidation = useRequiredValidation(intl);
  const phoneValidationRules = usePhoneValidationRules(intl);

  const emailValidationResult = useEmailValidationStatus(form, doesUserExist, checkingIfUserExists);

  return (
    <div className={cn(styles.stepContainer, className)}>
      <p className={commonStyles.description}>{title}</p>
      <FormItem
        colon={false}
        hasFeedback
        label={intl.formatMessage({
          id: "auth.register.contactInfoStep.fullname",
          defaultMessage: "Full Name",
          description: "Title of the full name field on the register form",
        })}
        name={SignupField.FULL_NAME}
        requiredMark="optional"
        rules={requiredValidation}
      >
        <Input autoComplete="name" size="large" />
      </FormItem>
      <FormItem
        colon={false}
        hasFeedback
        label={intl.formatMessage({
          id: "auth.register.contactInfoStep.email",
          defaultMessage: "Email",
          description: "Title of the Email field on the register form",
        })}
        name={SignupField.EMAIL}
        requiredMark="optional"
        rules={usernameValidationRules}
        validateStatus={emailValidationResult}
      >
        <Input
          autoComplete="username"
          disabled={!!email}
          onChange={checkExistingUser}
          size="large"
        />
      </FormItem>
      <FormItem
        colon={false}
        hasFeedback
        label={intl.formatMessage({
          id: "auth.register.contactInfoStep.phone",
          defaultMessage: "Phone",
          description: "Title of the Phone number field on the register form",
        })}
        name={SignupField.PHONE}
        requiredMark="optional"
        rules={phoneValidationRules}
      >
        <Input autoComplete="tel" name="phone" size="large" type="tel" />
      </FormItem>
      <FormItem>
        <div className={styles.buttons}>
          <Button disabled={!isValid} htmlType="button" onClick={onNextClick} type="primary">
            <FormattedMessage
              id="auth.register.nextStepButton"
              defaultMessage="Next Step"
              description="Title of the Next Step button for the Register Form"
            />
          </Button>
        </div>
      </FormItem>
      <div className={styles.backToLoginButton}>
        <FormattedMessage
          id="auth.register.backToLogin"
          defaultMessage="Already have an account? <link>Log in</link>"
          description="Title of the Back to Login Link"
          values={{
            link: (text: ReactNode) => <Link to={Path.LOGIN}>{text}</Link>,
          }}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  checkingIfUserExists: isUserExistsLoading(state),
  doesUserExist: doesUserExist(state),
});

const mapDispatchToProps = {
  userExists: userExists.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(ContactInfoStep);
